import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deleteHero,
  deleteStepPhoto,
  updateStepPhoto,
  uploadHero,
  uploadStepPhoto,
} from '@/api/photos'
import type { ProjectDetail } from '@/api/types'
import { PROJECTS_QUERY_KEY } from './use-projects'

/**
 * Phase 8 — TanStack Query mutations for Step Photos and Project Hero. Each mutation returns the
 * fresh {@link ProjectDetail}; we set it directly into the project's detail cache and invalidate
 * the broader project / browse / followings caches so hero thumbs propagate everywhere.
 */

const setProjectDetail = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  data: ProjectDetail
) => {
  queryClient.setQueryData([...PROJECTS_QUERY_KEY, 'detail', projectId], data)
  // The /p/:handle/:slug query key isn't keyed on UUID, so we invalidate the broader project
  // namespace to refresh that variant too.
  void queryClient.invalidateQueries({ queryKey: [...PROJECTS_QUERY_KEY, 'by-handle-slug'] })
  void queryClient.invalidateQueries({ queryKey: ['browse'] })
  void queryClient.invalidateQueries({ queryKey: ['followings'] })
  void queryClient.invalidateQueries({ queryKey: ['users'] })
}

export const useUploadStepPhoto = (projectId: string, stepId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ blob, caption }: { blob: Blob; caption?: string | null }) =>
      uploadStepPhoto(projectId, stepId, blob, caption),
    onSuccess: (data) => setProjectDetail(queryClient, projectId, data),
  })
}

export const useUpdateStepPhoto = (projectId: string, stepId: string, photoId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: { caption?: string | null; sortOrder?: number }) =>
      updateStepPhoto(projectId, stepId, photoId, patch),
    onSuccess: (data) => setProjectDetail(queryClient, projectId, data),
  })
}

export const useDeleteStepPhoto = (projectId: string, stepId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (photoId: string) => deleteStepPhoto(projectId, stepId, photoId),
    onSuccess: (data) => setProjectDetail(queryClient, projectId, data),
  })
}

export const useUploadHero = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (blob: Blob) => uploadHero(projectId, blob),
    onSuccess: (data) => setProjectDetail(queryClient, projectId, data),
  })
}

export const useDeleteHero = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteHero(projectId),
    onSuccess: (data) => setProjectDetail(queryClient, projectId, data),
  })
}
